// src/review/canvas/sections/RiskOverviewSection.jsx
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Card, Stack, Grid } from '@mantine/core';
import { useDispatch } from 'react-redux';
import EditableField from '../shared/EditableField';
import { persistRiskName } from '../../../redux/slices/risksSlice';
import { useReview } from '../../context/ReviewContext';

/**
 * RiskOverviewSection - Shows basic risk information
 * Displays risk scenario, source, area
 */
function RiskOverviewSection({ risk }) {
  const dispatch = useDispatch();
  const { modelId, assessmentUuid } = useReview();

  // Save handler for risk name
  const handleSaveRiskName = useCallback(async (newValue, oldValue) => {
    await dispatch(persistRiskName({
      modelId,
      assessmentUuid,
      riskId: risk.riskId,
      riskName: newValue,
      sectionName: risk.areaName,
      category: risk.category
    }));
  }, [dispatch, modelId, assessmentUuid, risk.riskId, risk.areaName, risk.category]);

  return (
    <Card withBorder p="md">
      <Stack gap="md">
        <EditableField
          label="Risk Name"
          value={risk.riskName}
          itemType="risk"
          itemId={risk.riskId}
          fieldName="riskName"
          multiline
          minRows={3}
          placeholder="Describe the risk..."
          onSave={handleSaveRiskName}
        />

        <Grid gutter="md">
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="xs">
              <EditableField
                label="Risk Source"
                value={risk.sourceName || risk.category}
                itemType="risk"
                itemId={risk.riskId}
                fieldName="sourceName"
                readOnly
              />
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="xs">
              <EditableField
                label="Risk Area"
                value={risk.areaName}
                itemType="risk"
                itemId={risk.riskId}
                fieldName="areaName"
                readOnly
              />
            </Stack>
          </Grid.Col>
        </Grid>

        <EditableField
          label="Priority"
          value={risk.priority}
          itemType="risk"
          itemId={risk.riskId}
          fieldName="priority"
          readOnly
        />
      </Stack>
    </Card>
  );
}

RiskOverviewSection.propTypes = {
  risk: PropTypes.shape({
    riskId: PropTypes.string.isRequired,
    riskName: PropTypes.string,
    sourceName: PropTypes.string,
    category: PropTypes.string,
    areaName: PropTypes.string,
    priority: PropTypes.string,
  }).isRequired,
};

export default RiskOverviewSection;
